import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

type YoutubeIframeProps = {
    videoId: string;
    className?: string;
    style?: React.CSSProperties;
    muted?: boolean;
}

export interface YoutubeIframeRef {
    play: () => void;
    pause: () => void;
    seekTo: (seconds: number) => void;
    isMuted: () => boolean;
    mute: () => void;
    unmute: () => void;
    getPlayer: () => any;
}

const YoutubeIframe = forwardRef<YoutubeIframeRef, YoutubeIframeProps>(({
    videoId,
    className,
    style
}, ref) => {
    const playerRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const playerIdRef = useRef<string>(`youtube-player-${Math.random().toString(36).substr(2, 9)}`);
    const isReady = useRef<boolean>(false);
    const pendingSeek = useRef<number | null>(null);
    const pendingPlay = useRef<boolean>(false);

    useImperativeHandle(ref, () => ({
        play: () => {
            if (playerRef.current && isReady.current) {
                playerRef.current.playVideo();
            } else {
                pendingPlay.current = true;
            }
        },
        pause: () => {
            if (playerRef.current && isReady.current) {
                playerRef.current.pauseVideo();
            }
            pendingPlay.current = false;
        },
        seekTo: (seconds: number) => {
            if (playerRef.current && isReady.current) {
                // Usar el API de YouTube para buscar un momento específico
                playerRef.current.seekTo(seconds, true);
            } else {
                pendingSeek.current = seconds;
            }
        },
        isMuted: () => {
            return playerRef.current ? playerRef.current.isMuted() : false;
        },
        mute: () => {
            if (playerRef.current && isReady.current) {
                playerRef.current.mute();
            }
        },
        unmute: () => {
            if (playerRef.current && isReady.current) {
                playerRef.current.unMute();
            }
        },
        getPlayer: () => playerRef.current
    }));

    useEffect(() => {
        // Cargar la API de YouTube IFrame
        if (!(window as any).YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        }

        const onYouTubeIframeAPIReady = () => {
            playerRef.current = new (window as any).YT.Player(playerIdRef.current, {
                videoId: videoId,
                playerVars: {
                    autoplay: 0,
                    controls: 0,
                    disablekb: 1,
                    enablejsapi: 1,
                    fs: 0,
                    modestbranding: 1,
                    rel: 0,
                    cc_load_policy: 0,
                    iv_load_policy: 3,
                    playsinline: 1,
                    origin: window.location.origin
                },
                events: {
                    onReady: (event: any) => {
                        isReady.current = true;
                        event.target.mute();

                        // Establecer la mayor calidad disponible
                        event.target.setPlaybackQuality('highres');

                        // Aplicar acciones pendientes cuando el reproductor esté listo
                        if (pendingSeek.current !== null) {
                            event.target.seekTo(pendingSeek.current, true);
                            pendingSeek.current = null;
                        }

                        if (pendingPlay.current) {
                            event.target.playVideo();
                            pendingPlay.current = false;
                        }

                        event.target.unloadModule('captions');
                        event.target.unloadModule('cc');
                    },
                    onStateChange: (event: any) => {
                        // Manejar cambios de estado del reproductor de YouTube
                        // 3 = buffering
                        if (event.data === 3 && pendingSeek.current !== null) {
                            // Intentar aplicar la búsqueda pendiente después del buffering
                            setTimeout(() => {
                                if (pendingSeek.current !== null && playerRef.current) {
                                    playerRef.current.seekTo(pendingSeek.current, true);
                                    pendingSeek.current = null;
                                }
                            }, 200);
                        }

                        event.target.unloadModule("captions");
                        event.target.unloadModule("cc");
                    },
                    onError: (event: any) => {
                        console.error('YouTube player error:', event.data);
                    }
                }
            });
        };

        if ((window as any).YT && (window as any).YT.Player) {
            onYouTubeIframeAPIReady();
        } else {
            (window as any).onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
        }

        return () => {
            if (playerRef.current) {
                playerRef.current.destroy();
            }
        };
    }, []);

    // Manejar cambios en el videoId
    useEffect(() => {
        if (playerRef.current && isReady.current && videoId) {
            console.log('Changing video to:', videoId);
            if (document.hidden) {
                if (typeof playerRef.current.cueVideoById === 'function') {
                    playerRef.current.cueVideoById(videoId);
                }
            } else if (typeof playerRef.current.loadVideoById === 'function') {
                playerRef.current.loadVideoById(videoId);
            }
        }
    }, [videoId]);


    return (
        <div ref={containerRef} className={className} style={style}>
            <div id={playerIdRef.current}></div>
        </div>
    );
});

export default YoutubeIframe;
